export type ParsedChord = {
  accidental: string | null;
  chord_root: string | null;
  flavor: string | null;
  non_chord: string | null;
  type: string | null;
  base_note_raw: string | null;
  alt_chord_raw: string | null;
  errors: string[];
};

export default class ChordParser {
  private raw_orig: string;
  private raw_remainder: string;
  parsed: ParsedChord;

  private readonly acceptable_chord_roots = "1234567ABCDEFG";
  private readonly recognized_non_chords = ['.', '-', 'br', '=', '%', 'w', '|'];
  private readonly accidentals_map: Record<string, string> = { '#': '♯', 'b': '♭' };
  private readonly accidentals = Object.keys(this.accidentals_map);

  constructor(beat_text?: string) {
    this.raw_orig = beat_text || '';
    this.raw_remainder = beat_text || '';
    this.parsed = {
      accidental: null,
      chord_root: null,
      flavor: null,
      non_chord: null,
      type: null,
      base_note_raw: null,
      alt_chord_raw: null,
      errors: [],
    };

    if (this.raw_remainder.length === 0) return;

    this.checkForErrors();

    if (this.parsed.errors.length === 0) {
      this.parseBeatText();
      this.injectChordTypeSymbols();
    }
  }

  noteWithAccidental = (): string => `${this.parsed.chord_root}${this.parsed.accidental}`;

  private removeSpaces = (): void => {
    this.raw_remainder = this.raw_remainder.replace(/\s/g, '');
    this.log('removeSpaces');
  };

  // an alt chord is written with parens: (A#9)
  private extractAltChord = (): void => {
    const alt_chord_regex = /\(.*\)/;
    const alt_chord_results = this.raw_remainder.match(alt_chord_regex);
    if (alt_chord_results) {
      let alt_chord_raw = alt_chord_results[0];
      alt_chord_raw = alt_chord_raw.substring(1, alt_chord_raw.length - 1); // remove parens
      this.parsed.alt_chord_raw = alt_chord_raw;
      this.raw_remainder = this.raw_remainder.replace(alt_chord_regex, '');
    }
    this.log('extractAltChord');
  };

  // base note is written as: /5
  private extractBaseNote = (): void => {
    const base_note_regex = /\/[A-Ga-g\d][b#]?/;
    const base_note_results = this.raw_remainder.match(base_note_regex);
    if (base_note_results) {
      let base_note_raw = base_note_results[0];
      base_note_raw = base_note_raw.substring(1); // remove slash
      this.parsed.base_note_raw = base_note_raw;
      this.raw_remainder = this.raw_remainder.replace(base_note_regex, '');
    }
    this.log('extractBaseNote');
  };

  // if index 0 or 1 is # or b
  private extractAccidental = (): void => {
    const chord_chars = this.raw_remainder.split('');
    if (this.accidentals.includes(chord_chars[0])) {
      this.extractAccidentalByIndex(0);
    } else if (this.accidentals.includes(chord_chars[1])) {
      this.extractAccidentalByIndex(1);
    }
    this.log('extractAccidental');
  };

  private extractAccidentalByIndex = (i: number): void => {
    const chord_chars = this.raw_remainder.split('');
    this.parsed.accidental = chord_chars[i] ?? null;
    chord_chars.splice(i, 1);
    this.raw_remainder = chord_chars.join('');
  };

  // assumes root is the first char at this point
  private extractChordRoot = (): void => {
    const chord_chars = this.raw_remainder.split('');
    const chord_root = chord_chars[0] || '';
    if (!this.acceptable_chord_roots.includes(chord_root)) return;
    this.parsed.chord_root = chord_chars[0] ?? null;
    chord_chars.splice(0, 1);
    this.raw_remainder = chord_chars.join('');
    this.log('extractChordRoot');
  };

  // assumes all remaining chord info is flavor info at this point
  private extractChordFlavor = (): void => {
    this.parsed.flavor = this.raw_remainder || null;
    this.raw_remainder = '';
    this.log('extractChordFlavor');
  };

  private checkForErrors = (): void => { };

  private parseBeatText = (): void => {
    if (this.isRecognizeableNonChord()) {
      this.setNonChord();
    } else {
      this.parseAsChord();
    }
  };

  private injectChordTypeSymbols = (): void => {
    let s = this.parsed.flavor;
    if (!s) return;
    s = s.replace('@', 'Ø');
    s = s.replace('b', '♭');
    s = s.replace('^', '△');
    this.parsed.flavor = s;
  };

  private parseAsChord = (): void => {
    this.extractAltChord();
    this.extractBaseNote();
    this.extractAccidental();
    this.extractChordRoot();
    this.removeSpaces();
    this.extractChordFlavor();
  };

  private isRecognizeableNonChord = (): boolean =>
    this.recognized_non_chords.includes(this.raw_orig);

  private setNonChord = (): void => {
    this.parsed.type = 'non_chord';
    this.parsed.non_chord = this.raw_orig;
  };

  symbolKey = (): [string, string][] => [
    ['#', '♯'],
    ['b', '♭'],
    ['@', 'Ø'],
    ['^', '△'],
    ['o', 'o'],
    ['(G)', 'alt chord'],
  ];

  private log = (_where: string): void => {
    // console.log(_where)
  };
}

